import { useId, useLayoutEffect, useRef, type ReactNode, type SyntheticEvent } from "react";
import { useT } from "../../i18n/locale";
import { IconButton } from "./IconButton";
import "./sheet.css";

export type SheetProps = {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
};

/** Le larghezze di una finestra (`dialog.css`): «normal», «wide» per moduli e liste, «table» per una tabella a più colonne. */
export type SheetSize = "normal" | "wide" | "table";

type Props = SheetProps & { kind: "dialog" | "drawer"; size?: SheetSize };

// Il corpo comune di Dialog e Drawer: un <dialog> nativo modale con intestazione (titolo e «Chiudi»),
// corpo che scorre e piede. Il componente esiste soltanto mentre la finestra è aperta: a ogni apertura
// nasce da capo (i campi non ricordano la volta prima) e alla chiusura sparisce dalla pagina.
// Chi lo usa passa `open` e `onClose`; la finestra non si chiude mai da sola, la chiude il padre.
export function ModalSheet({ kind, size, title, onClose, children, footer }: Props) {
  const t = useT();
  const titleId = useId();
  const dialogRef = useRef<HTMLDialogElement>(null);
  // Quante volte il componente ha chiuso la finestra da sé (alla sparizione) senza che l'evento `close`
  // sia ancora arrivato: il browser lo emette in un secondo momento, anche dopo che la finestra è stata
  // riaperta (in StrictMode l'effetto parte, si smonta e riparte). Quegli eventi non sono richieste dell'utente.
  const expectedCloses = useRef(0);
  // Esc non annullabile: il browser chiude comunque e subito dopo arriva `close`; il padre già lo sa.
  const closeAlreadyAsked = useRef(false);
  // L'ultimo `onClose` del padre, per i listener registrati una volta sola qui sotto.
  const onCloseRef = useRef(onClose);
  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  });

  // In un effetto di layout, così alla chiusura il focus torna a chi l'ha aperta prima del disegno
  // successivo e mentre la finestra è ancora nella pagina (il browser lo fa da sé con close(), ma
  // non ovunque: con il mouse Safari non dà il focus ai pulsanti, e allora non c'è nulla da ripristinare).
  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const active = document.activeElement;
    const opener = active instanceof HTMLElement && active !== document.body ? active : null;
    dialog.showModal();

    // Il clic sul fondo, che il browser attribuisce alla finestra stessa: la scheda interna la copre
    // tutta, quindi un clic il cui bersaglio è la finestra è un clic fuori dal contenuto. Vale soltanto se
    // anche il tasto è stato premuto sul fondo: trascinare la selezione di un campo fuori dalla finestra
    // e rilasciare lì non deve chiuderla. È una comodità del mouse (da tastiera c'è Esc), per questo sta
    // qui come listener e non come gestore JSX su un elemento che non è un controllo.
    let pressedOnBackdrop = false;
    const handleMouseDown = (event: MouseEvent) => {
      pressedOnBackdrop = event.target === dialog;
    };
    const handleClick = (event: MouseEvent) => {
      if (event.target === dialog && pressedOnBackdrop) onCloseRef.current();
      pressedOnBackdrop = false;
    };
    dialog.addEventListener("mousedown", handleMouseDown);
    dialog.addEventListener("click", handleClick);

    return () => {
      dialog.removeEventListener("mousedown", handleMouseDown);
      dialog.removeEventListener("click", handleClick);
      if (dialog.open) {
        expectedCloses.current += 1;
        dialog.close();
      }
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  function handleCancel(event: SyntheticEvent<HTMLDialogElement>) {
    // Esc: il browser chiuderebbe la finestra da sé; qui decide il padre, con `open`.
    event.preventDefault();
    if (!event.nativeEvent.cancelable) closeAlreadyAsked.current = true;
    onClose();
  }

  function handleNativeClose() {
    if (expectedCloses.current > 0) {
      expectedCloses.current -= 1;
      return;
    }
    // Il browser ha chiuso la finestra senza passare da un `cancel` annullabile (secondo Esc di fila
    // in Chrome, modulo con method="dialog"): il padre deve saperlo, una volta sola.
    if (closeAlreadyAsked.current) closeAlreadyAsked.current = false;
    else onClose();
    // Se il padre non ha chiuso (ha ancora `open`) la finestra non deve restare un guscio chiuso.
    // Il controllo aspetta che React abbia applicato l'eventuale chiusura: allora il riferimento è nullo.
    queueMicrotask(() => {
      const dialog = dialogRef.current;
      if (dialog?.isConnected && !dialog.open) dialog.showModal();
    });
  }

  return (
    <dialog
      ref={dialogRef}
      className={`g-${kind}`}
      data-size={size}
      aria-labelledby={titleId}
      onCancel={handleCancel}
      onClose={handleNativeClose}
    >
      <div className="g-sheet">
        <div className="g-sheet__head">
          <h2 id={titleId} className="g-sheet__title">{title}</h2>
          <IconButton className="g-sheet__close" label={t("common.close")} icon="close" iconSize="md" size="md" tone="muted" onClick={onClose} />
        </div>
        <div className="g-sheet__body">{children}</div>
        {footer ? <div className="g-sheet__foot">{footer}</div> : null}
      </div>
    </dialog>
  );
}
